import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion, type Variants } from 'framer-motion';
import { cn } from '../../utils/cn';
import {
  defaultViewport,
  fadeIn,
  fadeLeft,
  fadeRight,
  fadeUp,
  scaleIn,
  staggerContainer,
  staggerItem,
  sectionReveal,
} from './motionVariants';

type RevealDirection = 'up' | 'left' | 'right' | 'scale' | 'fade';

const directionMap: Record<RevealDirection, Variants> = {
  up: fadeUp,
  left: fadeLeft,
  right: fadeRight,
  scale: scaleIn,
  fade: fadeIn,
};

/** Replays entrance when scrolling back into view — without exit animations that break React DOM */
function useReplayInView<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  const isInView = useInView(ref, { ...defaultViewport, once: false });
  const [cycle, setCycle] = useState(0);
  const wasInView = useRef(false);

  useEffect(() => {
    if (isInView && !wasInView.current) {
      setCycle((c) => c + 1);
    }
    wasInView.current = isInView;
  }, [isInView]);

  return { ref, cycle };
}

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  direction?: RevealDirection;
  delay?: number;
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className,
  direction = 'up',
  delay = 0,
}) => {
  const reduceMotion = useReducedMotion();
  const { ref, cycle } = useReplayInView<HTMLDivElement>();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      key={cycle}
      className={className}
      initial="hidden"
      animate="visible"
      variants={directionMap[direction]}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
};

interface ScrollStaggerProps {
  children: React.ReactNode;
  className?: string;
  stagger?: number;
}

export const ScrollStagger: React.FC<ScrollStaggerProps> = ({
  children,
  className,
  stagger = 0.1,
}) => {
  const reduceMotion = useReducedMotion();
  const { ref, cycle } = useReplayInView<HTMLDivElement>();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      key={cycle}
      className={className}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: { staggerChildren: stagger, delayChildren: 0.06 },
        },
      }}
    >
      {children}
    </motion.div>
  );
};

export const ScrollStaggerItem: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div variants={staggerItem} className={className}>
      {children}
    </motion.div>
  );
};

interface ScrollSectionProps {
  children: React.ReactNode;
  id?: string;
  className?: string;
}

/** Full-width section with subtle scroll entrance */
export const ScrollSection: React.FC<ScrollSectionProps> = ({ children, id, className }) => {
  const reduceMotion = useReducedMotion();
  const { ref, cycle } = useReplayInView<HTMLDivElement>();

  if (reduceMotion) {
    return (
      <section id={id} className={className}>
        {children}
      </section>
    );
  }
  return (
    <motion.section
      ref={ref}
      key={cycle}
      id={id}
      className={cn(className)}
      initial="hidden"
      animate="visible"
      variants={sectionReveal}
    >
      {children}
    </motion.section>
  );
};

export { staggerContainer, staggerItem };
